import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, Typography, Checkbox, FormControlLabel,
    Table, TableHead, TableBody, TableRow, TableCell,
    Chip, TextField, Paper, CircularProgress, Alert,
    Stack, Autocomplete, Switch
} from '@mui/material';
import * as api from './rolesApi';
import type { Role, Claim, Widget, DataSource } from './rolesApi';
import { sx13, paperSx } from '../common/detailView';


interface ClaimsRow {
    objectType: string;
    label: string;
    read: boolean;
    write: boolean;
}

const READ_TYPES = ['read', 'readList'];
const WRITE_TYPES = ['create', 'update', 'delete'];

interface Props {
    roleId: number | null;
    canUpdate?: boolean;
}

export function RoleView({ roleId, canUpdate = false }: Props) {
    const { t } = useTranslation();

    const [role, setRole] = useState<Role | null>(null);
    const [allClaims, setAllClaims] = useState<Claim[]>([]);
    const [allWidgets, setAllWidgets] = useState<Widget[]>([]);
    const [allDataSources, setAllDataSources] = useState<DataSource[]>([]);
    const [claimsRows, setClaimsRows] = useState<ClaimsRow[]>([]);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const buildClaimsRows = (claims: Claim[], roleClaims: Claim[]): ClaimsRow[] => {
        const objectTypes = [...new Set(claims.map(c => c.objectType))];
        const claimTypes = [...new Set(claims.map(c => c.claimType))];

        return objectTypes.map(objectType => {
            let read = false;
            let write = false;

            for (const claimType of claimTypes) {
                const hasClaim = roleClaims.some(
                    rc => rc.claimType === claimType && rc.objectType === objectType
                );
                if (hasClaim) {
                    if (READ_TYPES.includes(claimType)) read = true;
                    else if (WRITE_TYPES.includes(claimType)) write = true;
                }
            }

            return {
                objectType,
                label: t('role.' + objectType),
                read,
                write,
            };
        });
    };

    const loadData = useCallback(async () => {
        if (roleId == null) return;
        setLoading(true);
        setError(null);
        try {
            const [roleData, claimsData, widgetsData, dataSourcesData] = await Promise.all([
                api.getRole(roleId),
                api.getAllClaims(),
                api.getAllWidgets(),
                api.getAllDataSources(),
            ]);

            setRole(roleData);
            setAllClaims(claimsData);
            setAllWidgets(widgetsData);
            setAllDataSources(dataSourcesData);
            setClaimsRows(buildClaimsRows(claimsData, roleData.claims ?? []));
        } catch (err) {
            setError(String(err));
        } finally {
            setLoading(false);
        }
    }, [roleId]);

    useEffect(() => { loadData(); }, [loadData]);

    const checkChanged = (objectType: string, type: 'read' | 'write') => {
        setClaimsRows(prev => prev.map(row => {
            if (row.objectType !== objectType) return row;
            if (type === 'read') {
                const newRead = !row.read;
                return { ...row, read: newRead, write: newRead ? row.write : false };
            } else {
                const newWrite = !row.write;
                return { ...row, write: newWrite, read: newWrite ? true : row.read };
            }
        }));
    };

    const saveRole = async () => {
        if (!role || saving) return;
        setSaving(true);
        setError(null);
        try {
            const claimsToSave: Claim[] = [];

            for (const row of claimsRows) {
                const typesToAdd: string[] = [];
                if (row.read) typesToAdd.push(...READ_TYPES);
                if (row.write) typesToAdd.push(...WRITE_TYPES);

                for (const claimType of typesToAdd) {
                    const found = allClaims.find(
                        c => c.objectType === row.objectType && c.claimType === claimType
                    );
                    if (found) claimsToSave.push(found);
                }
            }

            const updatedRole: Role = { ...role, claims: claimsToSave };
            await api.updateRole(updatedRole);
            setRole(updatedRole);
        } catch (err) {
            setError(String(err));
        } finally {
            setSaving(false);
        }
    };

    const updateWidgets = (widgets: Widget[]) => {
        if (!role) return;
        setRole({ ...role, widgets });
    };

    const updateDataSources = (dataSources: DataSource[]) => {
        if (!role) return;
        setRole({ ...role, dataSources });
    };

    const toggleAdminRole = (checked: boolean) => {
        if (!role) return;
        setRole({ ...role, isAdminRole: checked });
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
    }
    if (error && !role) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (!role) return null;

    const sortedRows = [...claimsRows].sort((a, b) => a.label.localeCompare(b.label));

    return (
        <Paper variant="outlined" sx={paperSx}>
            {/* Header */}
            <Box px={2} py={1} display="flex" alignItems="center" gap={1} bgcolor="grey.100">
                <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    onClick={saveRole}
                    disabled={saving || !canUpdate}
                >
                    {t('common.save')}
                </Button>
                <Typography variant="h6" sx={{ fontSize: '14px', lineHeight: 1.43 }} fontWeight="bold">
                    {role.roleName}
                </Typography>
                {(
                    <FormControlLabel
                        sx={{ marginLeft: 'auto' }}
                        control={<Switch checked={true} onChange={() => { localStorage.setItem('devFeature_reactRoleView', 'false'); window.location.reload(); }} size="small" sx={{
                            width: 34, height: 20, padding: 0, minWidth: 34,
                            '& .MuiSwitch-switchBase': { padding: '2px', '&.Mui-checked': { transform: 'translateX(14px)', color: '#fff', '& + .MuiSwitch-track': { backgroundColor: '#1976d2', opacity: 1 } } },
                            '& .MuiSwitch-thumb': { width: 16, height: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
                            '& .MuiSwitch-track': { borderRadius: 10, backgroundColor: '#ccc', opacity: 1 }
                        }} />}
                        label={<Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>{t('common.tryNewFeatures')}</Typography>}
                    />
                )}
            </Box>

            <Box p={2}>
                <Stack spacing={3}>
                    {error && (
                        <Alert severity="error" onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {/* Admin Role Checkbox */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={role.isAdminRole}
                                onChange={e => toggleAdminRole(e.target.checked)}
                                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                            />
                        }
                        label={<Typography sx={sx13}>{t('role.isAdminRole')}</Typography>}
                    />

                    {/* Claims Grid */}
                    {!role.isAdminRole && (
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell sx={{ ...sx13, width: '50%' }}>&ensp;</TableCell>
                                    <TableCell sx={{ ...sx13, textAlign: 'center' }}>
                                        {t('role.read')}
                                    </TableCell>
                                    <TableCell sx={{ ...sx13, textAlign: 'center' }}>
                                        {t('role.write')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sortedRows.map((row, idx) => (
                                    <TableRow
                                        key={row.objectType}
                                        sx={{
                                            bgcolor: idx % 2 === 1 ? 'whitesmoke' : undefined,
                                            '&:hover': { bgcolor: 'rgb(235, 235, 235)' },
                                            '& td': { py: '6px', pb: '2px' },
                                        }}
                                    >
                                        <TableCell sx={{ ...sx13, pl: '20px' }}>
                                            {row.label}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Checkbox
                                                checked={row.read}
                                                onChange={() => checkChanged(row.objectType, 'read')}
                                                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Checkbox
                                                checked={row.write}
                                                onChange={() => checkChanged(row.objectType, 'write')}
                                                sx={{ color: '#1976d2', '&.Mui-checked': { color: '#1976d2' } }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}

                    {/* Widgets */}
                    <Box display="flex" gap={2} alignItems="flex-start">
                        <Box width={200} flexShrink={0}>
                            <Typography sx={sx13} fontWeight="bold">{t('role.widgetFilter')}</Typography>
                        </Box>
                        <Box flex={1}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...allWidgets].sort((a, b) => a.title.localeCompare(b.title))}
                                getOptionLabel={o => o.title}
                                value={role.widgets ?? []}
                                onChange={(_, value) => updateWidgets(value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('role.addWidgetFilter')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((w, i) => <Chip {...getTagProps({ index: i })} key={w.id} label={w.title} size="small" />)
                                }
                            />
                        </Box>
                    </Box>

                    {/* Data Sources */}
                    <Box display="flex" gap={2} alignItems="flex-start">
                        <Box width={200} flexShrink={0}>
                            <Typography sx={sx13} fontWeight="bold">{t('role.dataSourcesFilter')}</Typography>
                        </Box>
                        <Box flex={1}>
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...allDataSources].sort((a, b) => a.description.localeCompare(b.description))}
                                getOptionLabel={o => o.description}
                                value={role.dataSources ?? []}
                                onChange={(_, value) => updateDataSources(value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('role.addDataSourceFilter')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((ds, i) => <Chip {...getTagProps({ index: i })} key={ds.id} label={ds.description} size="small" />)
                                }
                            />
                        </Box>
                    </Box>
                </Stack>
            </Box>

            {saving && (
                <Box display="flex" justifyContent="center" p={1}>
                    <CircularProgress size={20} />
                </Box>
            )}
        </Paper>
    );
}
